import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import sb from '@/utils/sb'
import { supabaseAuthActions } from '@/utils/store/supabaseAuth'
import {
  type ActionFunctionArgs,
  Link,
  data,
  redirect,
  useFetcher,
  useSearchParams,
} from 'react-router'

export const action = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.formData()
  const step = formData.get('step') as string
  const supabase = sb;

  // 步骤1：发送验证码
  if (step === 'send-otp') {
    const email = formData.get('email') as string

    if (!email) {
      return { error: '邮箱不能为空' }
    }

    // 发送 OTP 验证码
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false, // 重置密码时不创建新用户
      },
    })

    if (error) {
      return { error: error.message }
    }

    return redirect(`/s_auth/forgot-password?step=verify&email=${encodeURIComponent(email)}`)
  }

  // 步骤2：验证验证码
  if (step === 'verify-otp') {
    const email = formData.get('email') as string
    const token = formData.get('token') as string

    if (!token) {
      return { error: '验证码不能为空' }
    }

    // 验证 OTP
    const { data: sessionData, error: verifyError } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    })

    if (verifyError) {
      return { error: verifyError.message }
    }

    // 验证成功，更新认证状态
    await supabaseAuthActions.update();

    // 重定向到密码更新页面
    return redirect('/s_auth/update-password')
  }

  return { error: '无效的操作' }
}

export default function ForgotPassword() {
  const fetcher = useFetcher<typeof action>()
  let [searchParams] = useSearchParams()

  const step = searchParams.get('step') || 'email'
  const email = searchParams.get('email') || ''
  const error = fetcher.data?.error
  const loading = fetcher.state === 'submitting'

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm">
        <div className="flex flex-col gap-6">
          {step === 'verify' ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">输入验证码</CardTitle>
                <CardDescription>
                  我们已向 {email} 发送了验证码
                </CardDescription>
              </CardHeader>
              <CardContent>
                <fetcher.Form method="post">
                  <input type="hidden" name="step" value="verify-otp" />
                  <input type="hidden" name="email" value={email} />
                  <div className="flex flex-col gap-6">
                    <div className="grid gap-2">
                      <Label htmlFor="token">验证码</Label>
                      <Input
                        id="token"
                        name="token"
                        type="text"
                        placeholder="请输入6位验证码"
                        maxLength={6}
                        required
                        autoFocus
                      />
                      <p className="text-xs text-muted-foreground">
                        请查看您的邮箱获取验证码
                      </p>
                    </div>
                    {error && <p className="text-sm text-red-500">{error}</p>}
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? '验证中...' : '验证并重置密码'}
                    </Button>
                  </div>
                  <div className="mt-4 text-center text-sm">
                    <Link 
                      to="/s_auth/forgot-password" 
                      className="underline underline-offset-4"
                    >
                      返回重新发送
                    </Link>
                  </div>
                </fetcher.Form>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">重置您的密码</CardTitle>
                <CardDescription>
                  输入您的邮箱，我们将发送一个验证码给您
                </CardDescription>
              </CardHeader>
              <CardContent>
                <fetcher.Form method="post">
                  <input type="hidden" name="step" value="send-otp" />
                  <div className="flex flex-col gap-6">
                    <div className="grid gap-2">
                      <Label htmlFor="email">邮箱</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="m@example.com"
                        defaultValue={email}
                        required
                        autoFocus
                      />
                      <p className="text-xs text-muted-foreground">
                        我们将向此邮箱发送验证码
                      </p>
                    </div>
                    {error && <p className="text-sm text-red-500">{error}</p>}
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? '发送中...' : '发送验证码'}
                    </Button>
                  </div>
                  <div className="mt-4 text-center text-sm">
                    已有账户？{' '}
                    <Link to="/s_auth/login" className="underline underline-offset-4">
                      登录
                    </Link>
                  </div>
                </fetcher.Form>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
